import React, { useContext, useEffect, useRef, useState } from "react";
import {
  FiChevronDown,
  FiHeart,
  FiSearch,
  FiSliders,
  FiX,
} from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/shop.scss";

const categoryOptions = Array.from(
  new Set([
    ...siteConfig.navigation.map((item) => item.category),
    ...productData.map((product) => product.category),
  ])
);

const SALE_CATEGORY = "Sale";

const materialOptions = [
  {
    value: "Bio-Baumwolle",
    label: "Bio-Baumwolle",
    matches: (material) => material.includes("bio-baumwolle"),
  },
  {
    value: "Leinen",
    label: "Leinen",
    matches: (material) => material.includes("leinen"),
  },
  {
    value: "Hanf",
    label: "Hanf",
    matches: (material) => material.includes("hanf"),
  },
  {
    value: "Wolle",
    label: "Wolle & Merino",
    matches: (material) =>
      material.includes("schurwolle") ||
      material.includes("merinowolle") ||
      /(^|[\s,;])wolle/.test(material),
  },
  {
    value: "Recycelt",
    label: "Recycelte Fasern",
    matches: (material) => material.includes("recycelt"),
  },
  {
    value: "Viskose",
    label: "Viskose",
    matches: (material) => material.includes("viskose"),
  },
];

const priceOptions = [
  { value: "", label: "Alle Preise", min: 0, max: Infinity },
  { value: "0-60", label: "Bis 60 €", min: 0, max: 60 },
  { value: "60-100", label: "60 € – 100 €", min: 60, max: 100 },
  { value: "100-150", label: "100 € – 150 €", min: 100, max: 150 },
  { value: "150-plus", label: "Ab 150 €", min: 150, max: Infinity },
];

const sortOptions = [
  { value: "relevance", label: "Empfehlung" },
  { value: "newest", label: "Neuheiten" },
  { value: "price-asc", label: "Preis aufsteigend" },
  { value: "price-desc", label: "Preis absteigend" },
  { value: "rating", label: "Beste Bewertung" },
];

const colorPalette = [
  "#ded8c9",
  "#7d8c70",
  "#26344a",
  "#b7735c",
  "#494b45",
  "#a69278",
];

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("de-DE")
    .trim();

const isSaleProduct = (product) =>
  Number(product.originalPrice) > Number(product.price) ||
  product.tags?.some((tag) => normalizeText(tag) === "sale");

const matchesCategories = (product, categories) =>
  !categories.length ||
  categories.some((category) =>
    category === SALE_CATEGORY
      ? isSaleProduct(product)
      : product.category === category
  );

const readListParam = (searchParams, key) =>
  (searchParams.get(key) || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

const compareSizes = (first, second) => {
  const namedSizes = ["XS", "S", "M", "L", "XL", "XXL", "Einheitsgröße"];
  const firstNamedIndex = namedSizes.indexOf(first);
  const secondNamedIndex = namedSizes.indexOf(second);

  if (firstNamedIndex >= 0 || secondNamedIndex >= 0) {
    if (firstNamedIndex < 0) return 1;
    if (secondNamedIndex < 0) return -1;
    return firstNamedIndex - secondNamedIndex;
  }

  return Number(first) - Number(second);
};

const priceFormatter = new Intl.NumberFormat(siteConfig.locale, {
  style: "currency",
  currency: siteConfig.currency,
});

function Shop() {
  const cartState = useContext(CartContext) || {};
  const wishlistItems = Array.isArray(cartState.wishlistItems)
    ? cartState.wishlistItems
    : [];
  const toggleWishlist = cartState.toggleWishlist || (() => {});
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const mobileFilterButtonRef = useRef(null);
  const filterDrawerRef = useRef(null);
  const drawerCloseButtonRef = useRef(null);

  const query = (searchParams.get("q") || "").trim();
  const selectedCategories = readListParam(searchParams, "category");
  const selectedSubcategories = readListParam(searchParams, "subcategory");
  const selectedMaterials = readListParam(searchParams, "material");
  const selectedSizes = readListParam(searchParams, "size");
  const selectedPrice = searchParams.get("price") || "";
  const requestedSortMode = searchParams.get("sort") || "relevance";
  const sortMode = sortOptions.some(
    (option) => option.value === requestedSortMode
  )
    ? requestedSortMode
    : "relevance";
  const viewMode = searchParams.get("view") === "product" ? "product" : "model";

  useEffect(() => {
    if (!isFilterOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    drawerCloseButtonRef.current?.focus();

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsFilterOpen(false);
        mobileFilterButtonRef.current?.focus();
      }

      if (event.key === "Tab" && filterDrawerRef.current) {
        const focusableElements = Array.from(
          filterDrawerRef.current.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, a[href]'
          )
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    const desktopMedia =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(min-width: 961px)")
        : null;
    const closeOnDesktop = (event) => {
      if (event.matches) setIsFilterOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    desktopMedia?.addEventListener?.("change", closeOnDesktop);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
      desktopMedia?.removeEventListener?.("change", closeOnDesktop);
    };
  }, [isFilterOpen]);

  const categoryScopedProducts = selectedCategories.length
    ? productData.filter((product) =>
        matchesCategories(product, selectedCategories)
      )
    : productData;
  const availableSubcategories = Array.from(
    new Set(categoryScopedProducts.map((product) => product.subcategory))
  ).sort((first, second) => first.localeCompare(second, "de-DE"));
  const sizeSourceProducts = categoryScopedProducts.length
    ? categoryScopedProducts
    : productData;
  const sizeOptions = Array.from(
    new Set(sizeSourceProducts.flatMap((product) => product.size || []))
  ).sort(compareSizes);

  const activePriceOption =
    priceOptions.find((option) => option.value === selectedPrice) ||
    priceOptions[0];
  const normalizedQuery = normalizeText(query);

  const filteredProducts = productData
    .filter((product) => {
      const searchableText = normalizeText(
        [
          product.name,
          product.category,
          product.subcategory,
          product.material,
          ...(product.tags || []),
          ...(product.badges || []),
          ...(product.description || []),
        ].join(" ")
      );
      const material = normalizeText(product.material);
      const price = Number(product.price);
      const matchesMaterial =
        !selectedMaterials.length ||
        selectedMaterials.some((value) =>
          materialOptions
            .find((option) => option.value === value)
            ?.matches(material)
        );

      return (
        (!normalizedQuery || searchableText.includes(normalizedQuery)) &&
        matchesCategories(product, selectedCategories) &&
        (!selectedSubcategories.length ||
          selectedSubcategories.includes(product.subcategory)) &&
        matchesMaterial &&
        (!selectedSizes.length ||
          selectedSizes.some((size) => product.size?.includes(size))) &&
        price >= activePriceOption.min &&
        price < activePriceOption.max
      );
    })
    .sort((first, second) => {
      if (sortMode === "price-asc") {
        return Number(first.price) - Number(second.price);
      }
      if (sortMode === "price-desc") {
        return Number(second.price) - Number(first.price);
      }
      if (sortMode === "rating") {
        return Number(second.rating) - Number(first.rating);
      }
      if (sortMode === "newest") {
        const firstIsNew = first.badges?.includes("Neu") ? 1 : 0;
        const secondIsNew = second.badges?.includes("Neu") ? 1 : 0;
        return secondIsNew - firstIsNew || second.id - first.id;
      }
      return first.id - second.id;
    });

  const setParam = (key, value, defaultValue = "") => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) {
      nextParams.delete(key);
    } else {
      nextParams.set(key, value);
    }
    setSearchParams(nextParams);
  };

  const toggleListParam = (key, value) => {
    const nextParams = new URLSearchParams(searchParams);
    const currentValues = readListParam(nextParams, key);
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];

    if (nextValues.length) {
      nextParams.set(key, nextValues.join(","));
    } else {
      nextParams.delete(key);
    }

    if (key === "category") nextParams.delete("subcategory");
    setSearchParams(nextParams);
  };

  const clearListParam = (key) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(key);
    setSearchParams(nextParams);
  };

  const clearAllFilters = () => {
    const nextParams = new URLSearchParams();
    if (sortMode !== "relevance") nextParams.set("sort", sortMode);
    if (viewMode !== "model") nextParams.set("view", viewMode);
    setSearchParams(nextParams);
  };

  const closeFilterDrawer = () => {
    setIsFilterOpen(false);
    mobileFilterButtonRef.current?.focus();
  };

  const activeFilterCount =
    selectedCategories.length +
    selectedSubcategories.length +
    selectedMaterials.length +
    selectedSizes.length +
    (selectedPrice ? 1 : 0);
  const hasFilters = activeFilterCount > 0 || Boolean(query);
  const currentCategory =
    selectedCategories.length === 1 ? selectedCategories[0] : "Shop";
  const pageTitle = query
    ? `Suchergebnisse für „${query}“`
    : selectedCategories.length === 1
    ? currentCategory
    : "Natürlich gut angezogen";

  const activeFilterChips = [
    ...(query ? [{ key: "q", value: query, label: `Suche: ${query}` }] : []),
    ...selectedCategories.map((value) => ({
      key: "category",
      value,
      label: value,
    })),
    ...selectedSubcategories.map((value) => ({
      key: "subcategory",
      value,
      label: value,
    })),
    ...selectedMaterials.map((value) => ({
      key: "material",
      value,
      label:
        materialOptions.find((option) => option.value === value)?.label || value,
    })),
    ...selectedSizes.map((value) => ({
      key: "size",
      value,
      label: `Größe ${value}`,
    })),
    ...(selectedPrice
      ? [
          {
            key: "price",
            value: selectedPrice,
            label: activePriceOption.label,
          },
        ]
      : []),
  ];

  const removeFilterChip = (key, value) => {
    if (key === "q" || key === "price") {
      setParam(key, "");
    } else {
      toggleListParam(key, value);
    }
  };

  const renderFilters = (idPrefix) => (
    <div className="shop-page__filter-groups">
      <details open>
        <summary>
          Kategorie
          <FiChevronDown aria-hidden="true" />
        </summary>
        <fieldset>
          <legend className="shop-page__sr-only">Kategorie auswählen</legend>
          {categoryOptions.map((category, index) => (
            <label key={category} htmlFor={`${idPrefix}-category-${index}`}>
              <span>
                <input
                  id={`${idPrefix}-category-${index}`}
                  type="checkbox"
                  checked={selectedCategories.includes(category)}
                  onChange={() => toggleListParam("category", category)}
                />
                <span>{category}</span>
              </span>
              <small>
                {
                  productData.filter((product) =>
                    matchesCategories(product, [category])
                  ).length
                }
              </small>
            </label>
          ))}
        </fieldset>
      </details>

      <details open>
        <summary>
          Produktart
          <FiChevronDown aria-hidden="true" />
        </summary>
        <fieldset>
          <legend className="shop-page__sr-only">Produktart auswählen</legend>
          {availableSubcategories.map((subcategory, index) => (
            <label
              key={subcategory}
              htmlFor={`${idPrefix}-subcategory-${index}`}
            >
              <span>
                <input
                  id={`${idPrefix}-subcategory-${index}`}
                  type="checkbox"
                  checked={selectedSubcategories.includes(subcategory)}
                  onChange={() =>
                    toggleListParam("subcategory", subcategory)
                  }
                />
                <span>{subcategory}</span>
              </span>
              <small>
                {
                  categoryScopedProducts.filter(
                    (product) => product.subcategory === subcategory
                  ).length
                }
              </small>
            </label>
          ))}
        </fieldset>
      </details>

      <details open>
        <summary>
          Material
          <FiChevronDown aria-hidden="true" />
        </summary>
        <fieldset>
          <legend className="shop-page__sr-only">Material auswählen</legend>
          {materialOptions.map((option, index) => (
            <label key={option.value} htmlFor={`${idPrefix}-material-${index}`}>
              <span>
                <input
                  id={`${idPrefix}-material-${index}`}
                  type="checkbox"
                  checked={selectedMaterials.includes(option.value)}
                  onChange={() => toggleListParam("material", option.value)}
                />
                <span>{option.label}</span>
              </span>
              <small>
                {
                  productData.filter((product) =>
                    option.matches(normalizeText(product.material))
                  ).length
                }
              </small>
            </label>
          ))}
        </fieldset>
      </details>

      <details open>
        <summary>
          Größe
          <FiChevronDown aria-hidden="true" />
        </summary>
        <fieldset className="shop-page__size-options">
          <legend className="shop-page__sr-only">Größe auswählen</legend>
          {sizeOptions.map((size, index) => (
            <label key={size} htmlFor={`${idPrefix}-size-${index}`}>
              <input
                id={`${idPrefix}-size-${index}`}
                type="checkbox"
                checked={selectedSizes.includes(size)}
                onChange={() => toggleListParam("size", size)}
              />
              <span>{size}</span>
            </label>
          ))}
        </fieldset>
      </details>

      <details open>
        <summary>
          Preis
          <FiChevronDown aria-hidden="true" />
        </summary>
        <fieldset>
          <legend className="shop-page__sr-only">Preisspanne auswählen</legend>
          {priceOptions.map((option, index) => (
            <label key={option.label} htmlFor={`${idPrefix}-price-${index}`}>
              <span>
                <input
                  id={`${idPrefix}-price-${index}`}
                  type="radio"
                  name={`${idPrefix}-price`}
                  checked={selectedPrice === option.value}
                  onChange={() => setParam("price", option.value)}
                />
                <span>{option.label}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </details>
    </div>
  );

  return (
    <main className="shop-page">
      <header className="shop-page__header">
        <nav className="shop-page__breadcrumb" aria-label="Brotkrümelnavigation">
          <ol>
            <li>
              <Link to="/">Startseite</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              {currentCategory === "Shop" ? (
                <span aria-current="page">Shop</span>
              ) : (
                <Link to="/shop">Shop</Link>
              )}
            </li>
            {currentCategory !== "Shop" && (
              <>
                <li aria-hidden="true">/</li>
                <li>
                  <span aria-current="page">{currentCategory}</span>
                </li>
              </>
            )}
          </ol>
        </nav>
        <h1>{pageTitle}</h1>
        <p>
          Entdecke moderne Naturmode aus sorgfältig ausgewählten Fasern – fair
          gefertigt, angenehm zu tragen und für ein langes Leben gestaltet von{" "}
          {siteConfig.brandName}.
        </p>
      </header>

      {availableSubcategories.length > 0 && (
        <nav className="shop-page__subcategory-pills" aria-label="Produktarten">
          <button
            type="button"
            className={!selectedSubcategories.length ? "is-active" : ""}
            aria-pressed={!selectedSubcategories.length}
            onClick={() => clearListParam("subcategory")}
          >
            Alle
          </button>
          {availableSubcategories.map((subcategory) => (
            <button
              type="button"
              key={subcategory}
              className={
                selectedSubcategories.includes(subcategory) ? "is-active" : ""
              }
              aria-pressed={selectedSubcategories.includes(subcategory)}
              onClick={() => toggleListParam("subcategory", subcategory)}
            >
              {subcategory}
            </button>
          ))}
        </nav>
      )}

      <section className="shop-page__catalog" aria-labelledby="results-heading">
        <div className="shop-page__toolbar">
          <p id="results-heading" aria-live="polite">
            <strong>{filteredProducts.length}</strong> Artikel
          </p>

          <button
            type="button"
            className="shop-page__mobile-filter-button"
            ref={mobileFilterButtonRef}
            aria-controls="mobile-filter-drawer"
            aria-expanded={isFilterOpen}
            onClick={() => setIsFilterOpen(true)}
          >
            <FiSliders aria-hidden="true" />
            Filter
            {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
          </button>

          <div className="shop-page__toolbar-actions">
            <div className="shop-page__view-toggle" role="group" aria-label="Bildansicht">
              <button
                type="button"
                className={viewMode === "model" ? "is-active" : ""}
                aria-pressed={viewMode === "model"}
                onClick={() => setParam("view", "model", "model")}
              >
                Model
              </button>
              <button
                type="button"
                className={viewMode === "product" ? "is-active" : ""}
                aria-pressed={viewMode === "product"}
                onClick={() => setParam("view", "product", "model")}
              >
                Produkt
              </button>
            </div>
            <label className="shop-page__sort">
              <span>Sortieren nach</span>
              <select
                value={sortMode}
                onChange={(event) =>
                  setParam("sort", event.target.value, "relevance")
                }
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FiChevronDown aria-hidden="true" />
            </label>
          </div>
        </div>

        {activeFilterChips.length > 0 && (
          <div className="shop-page__active-filters" aria-label="Aktive Filter">
            {activeFilterChips.map((filter) => (
              <button
                type="button"
                key={`${filter.key}-${filter.value}`}
                onClick={() => removeFilterChip(filter.key, filter.value)}
                aria-label={`${filter.label} entfernen`}
              >
                {filter.label}
                <FiX aria-hidden="true" />
              </button>
            ))}
            <button
              type="button"
              className="shop-page__clear-link"
              onClick={clearAllFilters}
            >
              Alle löschen
            </button>
          </div>
        )}

        <div className="shop-page__catalog-layout">
          <aside className="shop-page__sidebar" aria-label="Produkte filtern">
            <div className="shop-page__sidebar-heading">
              <strong>Filter</strong>
              {hasFilters && (
                <button type="button" onClick={clearAllFilters}>
                  Zurücksetzen
                </button>
              )}
            </div>
            {renderFilters("desktop")}
          </aside>

          <div className="shop-page__results">
            {filteredProducts.length > 0 ? (
              <div className="shop-page__product-grid" role="list">
                {filteredProducts.map((product) => {
                  const isFavorite = wishlistItems.includes(product.id);
                  const modelImage = product.images?.[0] || product.image;
                  const productImage = product.images?.[1] || modelImage;
                  const primaryImage =
                    viewMode === "product" ? productImage : modelImage;
                  const secondaryImage =
                    viewMode === "product" ? modelImage : productImage;

                  return (
                    <article
                      className="shop-page__product-card"
                      key={product.id}
                      role="listitem"
                    >
                      <div className="shop-page__product-media">
                        <Link
                          to={`/shopDetails/${product.id}`}
                          aria-label={`${product.name} ansehen`}
                        >
                          <img
                            className="shop-page__product-image shop-page__product-image--primary"
                            src={primaryImage}
                            alt={product.name}
                            width="1024"
                            height="1365"
                            loading="lazy"
                            decoding="async"
                          />
                          {secondaryImage !== primaryImage && (
                            <img
                              className="shop-page__product-image shop-page__product-image--secondary"
                              src={secondaryImage}
                              alt=""
                              width="1024"
                              height="1365"
                              loading="lazy"
                              decoding="async"
                            />
                          )}
                        </Link>
                        <div className="shop-page__badges">
                          {product.badges?.slice(0, 2).map((badge) => (
                            <span key={badge}>{badge}</span>
                          ))}
                        </div>
                        <button
                          type="button"
                          className={`shop-page__wishlist ${
                            isFavorite ? "is-active" : ""
                          }`}
                          aria-label={
                            isFavorite
                              ? `${product.name} aus der Merkliste entfernen`
                              : `${product.name} zur Merkliste hinzufügen`
                          }
                          aria-pressed={isFavorite}
                          onClick={() => toggleWishlist(product.id)}
                        >
                          <FiHeart aria-hidden="true" />
                        </button>
                      </div>

                      <div className="shop-page__product-info">
                        <div className="shop-page__product-meta">
                          <span>{product.subcategory}</span>
                          <span
                            className="shop-page__rating"
                            aria-label={`${product.rating} von 5 Sternen bei ${product.reviews} Bewertungen`}
                          >
                            <span aria-hidden="true">★</span>
                            {Number(product.rating).toLocaleString("de-DE", {
                              minimumFractionDigits: 1,
                            })}
                          </span>
                        </div>
                        <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                        <div
                          className={`shop-page__price-row${
                            Number(product.originalPrice) > Number(product.price)
                              ? " is-sale"
                              : ""
                          }`}
                        >
                          <strong>
                            {priceFormatter.format(Number(product.price))}
                          </strong>
                          {Number(product.originalPrice) > Number(product.price) && (
                            <del>
                              {priceFormatter.format(Number(product.originalPrice))}
                            </del>
                          )}
                        </div>
                        <div
                          className="shop-page__colors"
                          role="group"
                          aria-label={`${product.colors.length} Farben verfügbar`}
                        >
                          {product.colors.slice(0, 5).map((color, index) => (
                            <span
                              key={color}
                              role="img"
                              title={color}
                              aria-label={color}
                              style={{
                                "--swatch-color":
                                  colorPalette[(product.id + index) % colorPalette.length],
                              }}
                            />
                          ))}
                          <small>{product.colors.length} Farben</small>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="shop-page__empty" role="status">
                <FiSearch aria-hidden="true" />
                <h2>Keine passenden Artikel gefunden</h2>
                <p>
                  Ändere deine Auswahl oder entferne die Filter, um wieder alle
                  Lieblingsstücke zu sehen.
                </p>
                <button type="button" onClick={clearAllFilters}>
                  Filter zurücksetzen
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {isFilterOpen && (
        <>
          <button
            type="button"
            className="shop-page__drawer-scrim"
            aria-label="Filter schließen"
            onClick={closeFilterDrawer}
          />
          <aside
            className="shop-page__filter-drawer"
            id="mobile-filter-drawer"
            ref={filterDrawerRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-filter-heading"
          >
            <header>
              <h2 id="mobile-filter-heading">Filter</h2>
              <button
                type="button"
                ref={drawerCloseButtonRef}
                aria-label="Filter schließen"
                onClick={closeFilterDrawer}
              >
                <FiX aria-hidden="true" />
              </button>
            </header>
            <div className="shop-page__drawer-content">
              {renderFilters("mobile")}
            </div>
            <footer>
              {hasFilters && (
                <button
                  type="button"
                  className="shop-page__drawer-reset"
                  onClick={clearAllFilters}
                >
                  Zurücksetzen
                </button>
              )}
              <button
                type="button"
                className="shop-page__drawer-apply"
                onClick={closeFilterDrawer}
              >
                {filteredProducts.length} Artikel anzeigen
              </button>
            </footer>
          </aside>
        </>
      )}
    </main>
  );
}

export default Shop;
